import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDraftTelemetry, type DraftTelemetry } from '@/hooks/useDraftTelemetry';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ieltsApi,
  MIN_WORDS,
  type CriterionKey,
  type WritingSubmission,
  type WritingTask,
  type TrainerHandoff,
} from '@/api/ielts';
import { SelectionToolbar } from '@/components/ielts/SelectionToolbar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { clearWritingDraft, loadWritingDraft, saveWritingDraft, writingDraftKey } from '@/lib/writingDraft';
import { useOpenTest } from '@/hooks/useOpenTest';
import { useAuthStore } from '@/stores/auth';
import { SourceSplit } from '@/components/ielts/SourceSplit';

const TAB_IDS = ['write', 'history'] as const;
type TabId = (typeof TAB_IDS)[number];

const CRITERIA: CriterionKey[] = ['ta', 'cc', 'lr', 'gra'];

/** Count words the same simple way the editor hint does (whitespace split). */
function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/** Band → 0..1 fill for the criterion bars / overall ring (IELTS max is 9). */
function bandFraction(band?: number): number {
  if (typeof band !== 'number') return 0;
  return Math.max(0, Math.min(1, band / 9));
}

export function StudentIELTSWritingPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('write');

  const TABS: { id: TabId; label: string }[] = [
    { id: 'write', label: t('ielts.tabWrite') },
    { id: 'history', label: t('ielts.tabHistory') },
  ];

  return (
    <>
      <PageHeader title={t('ielts.title')} subtitle={t('ielts.subtitle')} />
      <div className="mb-6 flex gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label transition',
              tab === tb.id
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container-low',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'write' && <WriteTab />}
      {tab === 'history' && <HistoryTab />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Write tab: task picker → essay editor → result card
// ---------------------------------------------------------------------------

function WriteTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Приход из тренажёра: он уже выбрал задание по слабому типу, и каталог
  // здесь показывать нельзя — иначе выбор тренажёра просто теряется.
  const fromTrainer = (useLocation().state as { trainerPrompt?: TrainerHandoff } | null)?.trainerPrompt;

  // Задание из тренажёра живёт на состоянии: в каталоге его нет, адресом его не назвать.
  const [trainerTask, setTrainerTask] = useState<WritingTask | null>(
    fromTrainer
      ? {
          id: fromTrainer.id,
          task_type: fromTrainer.kind === 'task2' ? 'task2' : 'task1',
          title: fromTrainer.title || t('ielts.trainerTask'),
          prompt: fromTrainer.text,
          image_url: fromTrainer.image_url,
          is_active: true,
        }
      : null,
  );
  const [essay, setEssay] = useState('');
  const [result, setResult] = useState<WritingSubmission | null>(null);
  const userId = useAuthStore((s) => s.user?.id);

  // Computer-based-test shell: fullscreen "Focus" + draggable prompt/answer split.
  const promptRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);

  const tasks = useQuery({
    queryKey: ['ielts', 'tasks'],
    queryFn: () => ieltsApi.listTasks(),
  });

  // Открытое задание из каталога — шаг истории (`?test=<id>`, hooks/useOpenTest.ts): «назад» возвращает на список
  // заданий, а не уводит со страницы. Адрес с параметром после перезапуска или с чужим id — список.
  const openTask = useOpenTest(tasks.data?.map((x) => x.id));
  const selected: WritingTask | null = trainerTask ?? tasks.data?.find((x) => x.id === openTask.id) ?? null;

  /**
   * ⚠️ ЭССЕ — В ЧЕРНОВИКЕ НА УСТРОЙСТВЕ (02.10.2026, `lib/writingDraft.ts`). До этого текст жил только в памяти
   * экрана: «К заданиям», «назад» и перезапуск приложения стирали его без вопроса. Теперь он пишется на каждое
   * изменение, поднимается при открытии задания и стирается только после принятой сдачи.
   */
  const draftKey = selected ? writingDraftKey(userId, selected.id) : null;
  useEffect(() => {
    setEssay(draftKey ? loadWritingDraft(draftKey) : '');
    setResult(null);
    // Фокус-режим остаётся за открытым заданием: вернулись к списку — следующий редактор открывается обычным.
    setFocus(false);
  }, [draftKey]);

  // Счётчики о том, как писалось эссе. Уходят вместе с работой и видны только
  // куратору — ученику эти факты не показываются никогда.
  const draft = useDraftTelemetry();

  const submit = useMutation({
    mutationFn: (body: { task_id: string; essay_text: string; draft: DraftTelemetry }) =>
      ieltsApi.submit(body),
    onSuccess: (sub) => {
      setResult(sub);
      // Работа принята и оценена — черновик больше не нужен: иначе при следующем заходе старый текст лёг бы в
      // чистый бланк. Оценка не прошла (баннер «не удалось оценить») — текст остаётся и на экране, и в черновике.
      if (sub.status === 'graded' && draftKey) clearWritingDraft(draftKey);
      qc.invalidateQueries({ queryKey: ['ielts', 'history'] });
      qc.invalidateQueries({ queryKey: ['ielts', 'progress'] });
    },
  });

  const words = countWords(essay);
  const minWords = selected ? MIN_WORDS[selected.task_type] : 0;

  function startTask(task: WritingTask) {
    // Текст поднимет эффект выше — из черновика этого задания; пустой бланк, если черновика нет.
    openTask.open(task.id);
    setResult(null);
    submit.reset();
    // Счётчики обнуляем вместе с текстом: иначе второе эссе унаследует время
    // и вставки первого, и куратор увидит чужие факты.
    draft.reset();
  }

  // ⚠️ ЧЕРНОВИК ЗДЕСЬ НЕ СТИРАЕТСЯ: возврат к списку — не отказ от работы. Стирает его только принятая сдача.
  function backToPicker() {
    setResult(null);
    submit.reset();
    draft.reset();
    if (trainerTask) setTrainerTask(null);
    else openTask.close();
  }

  // ----- Task picker -----
  if (!selected) {
    if (tasks.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
    if (tasks.isError) {
      return (
        <Card variant="subtle" className="text-error">
          {humanizeError(tasks.error)}
        </Card>
      );
    }
    if (!tasks.data?.length) {
      return (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/60">
            edit_note
          </span>
          {t('ielts.noTasks')}
        </Card>
      );
    }
    return (
      <ul className="grid gap-3 md:grid-cols-2">
        {tasks.data.map((task) => (
          <li key={task.id}>
            <TaskCard task={task} onStart={() => startTask(task)} />
          </li>
        ))}
      </ul>
    );
  }

  // ----- Result card (after grading succeeds) -----
  if (result && result.status === 'graded') {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={backToPicker}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.backToTasks')}
        </Button>
        <ResultCard submission={result} />
      </div>
    );
  }

  // ----- Editor (engnovate-style prompt | answer split; failed-grading banner) -----
  const tooShort = words < minWords;
  const paneCls = focus ? 'h-[calc(100vh-7rem)] overflow-y-auto' : 'overflow-y-auto lg:max-h-[78vh]';
  return (
    <div className={focus ? 'fixed inset-0 z-50 flex flex-col gap-3 overflow-auto bg-background p-4' : 'space-y-4'}>
      {/* Test toolbar — exit · task badge · Focus fullscreen */}
      <div className="flex items-center justify-between gap-3 rounded-card bg-surface-container-lowest px-3 py-2 shadow-soft">
        <Button variant="ghost" size="sm" onClick={backToPicker} disabled={submit.isPending}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.backToTasks')}
        </Button>
        <TaskTypeBadge type={selected.task_type} />
        <Button variant="ghost" size="sm" onClick={() => setFocus((f) => !f)}>
          <span className="material-symbols-outlined text-base">{focus ? 'close_fullscreen' : 'open_in_full'}</span>
          {t('ielts.reading.focus')}
        </Button>
      </div>

      {/* Prompt | draggable divider | answer (computer-based-test split) */}
      {/* Задание и ответ. Раскладка общая с чтением, моком и спринтом.
          ВКЛАДКИ ЗДЕСЬ ВЫКЛЮЧЕНЫ намеренно: во время письма прятать условие
          за вкладку вредно — ученик сверяется с ним постоянно. На телефоне
          условие остаётся над полем ответа. */}
      <SourceSplit
        hideTabs
        className="flex min-h-0 flex-1 flex-col"
        source={
          <div className="relative">
            <SelectionToolbar containerRef={promptRef} />
            <Card className={paneCls}>
              <div ref={promptRef}>
                <div className="mb-2 flex items-center gap-2">
                  <TaskTypeBadge type={selected.task_type} />
                  {typeof selected.target_band === 'number' && (
                    <Pill variant="neutral">
                      {t('ielts.targetBand')} {selected.target_band.toFixed(1)}
                    </Pill>
                  )}
                </div>
                <CardTitle>{selected.title}</CardTitle>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-on-surface-variant">
                  {selected.prompt}
                </p>
                {selected.image_url && (
                  <img
                    src={selected.image_url}
                    alt={selected.title}
                    className="mt-4 w-full rounded-card object-contain"
                  />
                )}
              </div>
            </Card>
          </div>
        }
        questions={
          <>
            <Card className={cn(paneCls, 'flex flex-col')}>
              <textarea
                value={essay}
                onChange={(e) => {
                  setEssay(e.target.value);
                  if (draftKey) saveWritingDraft(draftKey, e.target.value);
                  draft.onChange(e);
                }}
                onPaste={draft.onPaste}
                placeholder={t('ielts.essayPlaceholder')}
                className="min-h-[16rem] w-full flex-1 resize-none rounded-button bg-surface-container-low px-4 py-3 text-sm leading-relaxed text-on-surface outline-none ring-1 ring-inset ring-outline-variant/30 focus:ring-2 focus:ring-primary"
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <span
                  className={cn(
                    'text-sm font-semibold',
                    tooShort ? 'text-on-surface-variant' : 'text-success',
                  )}
                >
                  {t('ielts.wordCount', { count: words })}
                  <span className="ml-2 font-normal text-on-surface-variant">
                    {t('ielts.minWords', { count: minWords })}
                  </span>
                </span>
                <Button
                  onClick={() =>
                    submit.mutate({
                      task_id: selected.id,
                      essay_text: essay,
                      draft: draft.snapshot(),
                    })
                  }
                  loading={submit.isPending}
                  disabled={submit.isPending || words === 0}
                >
                  {submit.isPending ? t('ielts.grading') : t('ielts.submit')}
                </Button>
              </div>
              {tooShort && words > 0 && (
                <p className="mt-2 text-xs text-on-surface-variant">{t('ielts.belowMinHint')}</p>
              )}
            </Card>
          </>
        }
      />

      {submit.isPending && (
        <Card variant="subtle" className="flex items-center gap-3 text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin text-primary">progress_activity</span>
          {t('ielts.gradingHint')}
        </Card>
      )}

      {submit.isError && (
        <Card variant="subtle" className="text-error">
          {humanizeError(submit.error)}
        </Card>
      )}

      {result && result.status === 'failed' && (
        <Card variant="subtle" className="text-error">
          {t('ielts.gradingFailed')}
        </Card>
      )}
    </div>
  );
}

function TaskCard({ task, onStart }: { task: WritingTask; onStart: () => void }) {
  const { t } = useTranslation();
  return (
    <Card interactive onClick={onStart} className="h-full cursor-pointer">
      <div className="mb-2 flex items-center gap-2">
        <TaskTypeBadge type={task.task_type} />
        {typeof task.target_band === 'number' && (
          <Pill variant="neutral">
            {t('ielts.targetBand')} {task.target_band.toFixed(1)}
          </Pill>
        )}
      </div>
      <CardTitle>{task.title}</CardTitle>
      <p className="mt-2 line-clamp-3 text-sm text-on-surface-variant">{task.prompt}</p>
      <div className="mt-4">
        <Button size="sm" variant="secondary" onClick={onStart}>
          {t('ielts.startWriting')}
        </Button>
      </div>
    </Card>
  );
}

function TaskTypeBadge({ type }: { type: WritingTask['task_type'] }) {
  const { t } = useTranslation();
  return (
    <Pill variant={type === 'task1' ? 'primary' : 'success'}>
      {type === 'task1' ? t('ielts.task1') : t('ielts.task2')}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Result card: overall band + criteria bars + errors + improved version
// ---------------------------------------------------------------------------

function ResultCard({ submission }: { submission: WritingSubmission }) {
  const { t } = useTranslation();
  const bands: Record<CriterionKey, number | undefined> = {
    ta: submission.band_ta,
    cc: submission.band_cc,
    lr: submission.band_lr,
    gra: submission.band_gra,
  };
  const fb = submission.feedback;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <OverallBand band={submission.band_overall} />
          <div className="flex-1">
            <CardTitle>{t('ielts.yourBand')}</CardTitle>
            {fb?.summary && (
              <p className="mt-2 text-sm text-on-surface-variant">{fb.summary}</p>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {CRITERIA.map((c) => (
            <CriterionBar
              key={c}
              label={t(`ielts.criterion.${c}`)}
              band={bands[c]}
              comment={fb?.criteria?.[c]}
            />
          ))}
        </div>
      </Card>

      {fb?.errors && fb.errors.length > 0 && (
        <Card>
          <CardTitle>{t('ielts.errorsTitle')}</CardTitle>
          <ul className="mt-3 space-y-3">
            {fb.errors.map((err, i) => (
              <li
                key={i}
                className="rounded-card bg-surface-container-low p-3 ring-1 ring-inset ring-outline-variant/20"
              >
                <div className="mb-1 flex items-center gap-2">
                  <Pill variant="warning">{t(`ielts.errorType.${err.type}`)}</Pill>
                </div>
                <p className="text-sm text-error line-through decoration-error/50">
                  {err.excerpt}
                </p>
                <p className="mt-1 text-sm font-semibold text-success">{err.correction}</p>
                <p className="mt-1 text-xs text-on-surface-variant">{err.explanation}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {fb?.improved_version && (
        <Card>
          <CardTitle>{t('ielts.improvedTitle')}</CardTitle>
          <p className="mt-3 whitespace-pre-wrap text-sm text-on-surface-variant">
            {fb.improved_version}
          </p>
        </Card>
      )}
    </div>
  );
}

function OverallBand({ band }: { band?: number }) {
  const { t } = useTranslation();
  const frac = bandFraction(band);
  // Inline conic-gradient ring — no chart dependency.
  const deg = Math.round(frac * 360);
  return (
    <div className="flex flex-col items-center">
      <div
        className="grid h-32 w-32 place-items-center rounded-full"
        style={{
          background: `conic-gradient(#FF941F ${deg}deg, rgba(127,127,127,0.18) ${deg}deg)`,
        }}
      >
        <div className="grid h-24 w-24 place-items-center rounded-full bg-surface-container-lowest">
          <span className="font-display text-4xl font-extrabold text-on-surface">
            {typeof band === 'number' ? band.toFixed(1) : '—'}
          </span>
        </div>
      </div>
      <span className="mt-2 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
        {t('ielts.overall')}
      </span>
    </div>
  );
}

function CriterionBar({
  label,
  band,
  comment,
}: {
  label: string;
  band?: number;
  comment?: string;
}) {
  const pct = Math.round(bandFraction(band) * 100);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm font-semibold text-on-surface">{label}</span>
        <span className="text-sm font-bold text-primary">
          {typeof band === 'number' ? band.toFixed(1) : '—'}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-pill bg-surface-container-low">
        <div className="h-full rounded-pill bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      {comment && <p className="mt-1 text-xs text-on-surface-variant">{comment}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab: list + band trend chart + weakest-criterion callout
// ---------------------------------------------------------------------------

function HistoryTab() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['ielts', 'history'],
    queryFn: () => ieltsApi.listSubmissions(),
  });
  const progress = useQuery({
    queryKey: ['ielts', 'progress'],
    queryFn: () => ieltsApi.getProgress(),
  });

  if (history.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (history.isError) {
    return (
      <Card variant="subtle" className="text-error">
        {humanizeError(history.error)}
      </Card>
    );
  }
  if (!history.data?.length) {
    return (
      <Card variant="subtle" className="text-center text-on-surface-variant">
        <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/60">
          history_edu
        </span>
        {t('ielts.noHistory')}
      </Card>
    );
  }

  const prog = progress.data;
  return (
    <div className="space-y-4">
      {prog && prog.count > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card variant="subtle">
            <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ielts.avgBand')}
            </p>
            <p className="mt-1 font-display text-3xl font-extrabold text-on-surface">
              {prog.avg_band.toFixed(1)}
            </p>
          </Card>
          <Card variant="subtle">
            <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ielts.latestBand')}
            </p>
            <p className="mt-1 font-display text-3xl font-extrabold text-on-surface">
              {prog.latest_band.toFixed(1)}
            </p>
          </Card>
          <Card variant="subtle">
            <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ielts.weakestCriterion')}
            </p>
            <p className="mt-1 text-lg font-bold text-error">
              {prog.weakest_criterion
                ? t(`ielts.criterion.${prog.weakest_criterion}`)
                : '—'}
            </p>
          </Card>
        </div>
      )}

      {prog && prog.band_trend.length > 1 && <BandTrendChart points={prog.band_trend} />}

      <ul className="space-y-2">
        {history.data.map((sub) => (
          <li key={sub.id}>
            <HistoryRow
              submission={sub}
              expanded={openId === sub.id}
              onToggle={() => setOpenId(openId === sub.id ? null : sub.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function BandTrendChart({ points }: { points: { date: string; band: number }[] }) {
  const { t } = useTranslation();
  const { W, H, PAD, path, dots } = useMemo(() => {
    const w = 520;
    const h = 160;
    const pad = 24;
    const n = points.length;
    const max = 9;
    const x = (i: number) => pad + (i * (w - pad * 2)) / Math.max(1, n - 1);
    const y = (b: number) => h - pad - (b / max) * (h - pad * 2);
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.band)}`).join(' ');
    const ds = points.map((p, i) => ({ cx: x(i), cy: y(p.band), band: p.band }));
    return { W: w, H: h, PAD: pad, path: d, dots: ds };
  }, [points]);

  return (
    <Card>
      <CardTitle>{t('ielts.bandTrend')}</CardTitle>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 h-40 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={t('ielts.bandTrend')}
      >
        {/* baseline */}
        <line
          x1={PAD}
          y1={H - PAD}
          x2={W - PAD}
          y2={H - PAD}
          stroke="currentColor"
          strokeOpacity={0.15}
        />
        <path d={path} fill="none" stroke="#FF941F" strokeWidth={2.5} />
        {dots.map((d, i) => (
          <g key={i}>
            <circle cx={d.cx} cy={d.cy} r={4} fill="#FF941F" />
            <text
              x={d.cx}
              y={d.cy - 8}
              textAnchor="middle"
              className="fill-current text-[10px]"
            >
              {d.band.toFixed(1)}
            </text>
          </g>
        ))}
      </svg>
    </Card>
  );
}

function HistoryRow({
  submission,
  expanded,
  onToggle,
}: {
  submission: WritingSubmission;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const detail = useQuery({
    queryKey: ['ielts', 'submission', submission.id],
    queryFn: () => ieltsApi.getSubmission(submission.id),
    enabled: expanded,
  });

  const date = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString()
    : '';

  return (
    <Card variant="subtle" className="overflow-hidden p-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-surface-container-low"
      >
        <div className="flex-1">
          <p className="font-semibold text-on-surface">
            {submission.task_title ?? t('ielts.essay')}
          </p>
          <p className="text-xs text-on-surface-variant">{date}</p>
        </div>
        {submission.status === 'graded' && typeof submission.band_overall === 'number' ? (
          <Pill variant="primary">{submission.band_overall.toFixed(1)}</Pill>
        ) : submission.status === 'grading' ? (
          <Pill variant="neutral">{t('ielts.grading')}</Pill>
        ) : (
          <Pill variant="danger">{t('ielts.failed')}</Pill>
        )}
        <span className="material-symbols-outlined text-on-surface-variant">
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-outline-variant/20 p-4">
          {detail.isLoading && (
            <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>
          )}
          {detail.isError && (
            <p className="text-sm text-error">{humanizeError(detail.error)}</p>
          )}
          {detail.data && detail.data.status === 'graded' && (
            <ResultCard submission={detail.data} />
          )}
          {detail.data && detail.data.status === 'failed' && (
            <p className="text-sm text-error">{t('ielts.gradingFailed')}</p>
          )}
        </div>
      )}
    </Card>
  );
}
